import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";
import "../css/contactus.scss";

const initialFormData = {
  name: "",
  email: "",
  orderNumber: "",
  topic: "",
  message: "",
};

const topics = [
  "Product question",
  "Shipping and delivery",
  "Return or exchange",
  "Order help",
  "Something else",
];

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const submitTimer = useRef(null);

  useEffect(
    () => () => {
      if (submitTimer.current) {
        window.clearTimeout(submitTimer.current);
      }
    },
    []
  );

  const validateForm = () => {
    const nextErrors = {};

    if (formData.name.trim().length < 2) {
      nextErrors.name = "Please enter your name.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!formData.topic) {
      nextErrors.topic = "Please choose a topic.";
    }

    if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add at least 10 characters.";
    }

    return nextErrors;
  };

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setShowSuccess(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(
        Object.keys(nextErrors)[0]
      );
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    submitTimer.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setShowSuccess(true);
      submitTimer.current = null;
    }, 650);
  };

  return (
    <main className="editorial-page contact-page">
      <div className="editorial-page__inner contact-page__inner">
        <header className="editorial-page__hero">
          <p className="editorial-page__eyebrow">Contact</p>
          <h1>How can we help?</h1>
          <p className="editorial-page__lede">
            Product details, order questions, or a note about your favorite pen—
            our small team would be glad to hear from you.
          </p>
        </header>

        <div className="editorial-page__rule" aria-hidden="true" />

        <div className="contact-layout">
          <aside className="contact-details" aria-label="Customer care details">
            <div>
              <p className="editorial-page__section-label">Email</p>
              <a href={"mailto:" + siteConfig.contact.supportEmail}>
                {siteConfig.contact.supportEmail}
              </a>
            </div>
            <div>
              <p className="editorial-page__section-label">Studio</p>
              <p>{siteConfig.contact.address}</p>
            </div>
            <p className="contact-response-note">
              We usually reply {siteConfig.contact.responseTime}, Monday through
              Friday.
            </p>
          </aside>

          <section
            className="contact-form-wrap"
            aria-labelledby="contact-form-title"
          >
            <h2 id="contact-form-title">Send a note</h2>

            {showSuccess && (
              <div className="contact-success" role="status">
                <strong>Your message details are ready.</strong>
                <span>
                  To reach our team directly, email {siteConfig.contact.supportEmail}.
                </span>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="contact-field">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <span className="contact-error" id="name-error">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="contact-field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <span className="contact-error" id="email-error">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="contact-field">
                <label htmlFor="orderNumber">
                  Order number <span>(optional)</span>
                </label>
                <input
                  id="orderNumber"
                  name="orderNumber"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. IL-1042"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                />
              </div>

              <div className="contact-field contact-field--select">
                <label htmlFor="topic">Topic</label>
                <select
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.topic)}
                  aria-describedby={errors.topic ? "topic-error" : undefined}
                >
                  <option value="">Select a topic</option>
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
                {errors.topic && (
                  <span className="contact-error" id="topic-error">
                    {errors.topic}
                  </span>
                )}
              </div>

              <div className="contact-field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="7"
                  value={formData.message}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={
                    errors.message ? "message-error" : "message-hint"
                  }
                />
                <span className="contact-hint" id="message-hint">
                  Please do not include payment-card details.
                </span>
                {errors.message && (
                  <span className="contact-error" id="message-error">
                    {errors.message}
                  </span>
                )}
              </div>

              <button
                className="contact-submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Preparing message…" : "Send message"}
              </button>
              <p className="contact-privacy">
                Read how we handle your information in our <Link to="/privacy">privacy policy</Link>.
              </p>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}

export default Contact;
